<template>
  <header :class="$style.wrap">
    <div :class="[$style.content]">
      <NuxtLinkLocale
        v-if="instanceOfAsset(data?.header?.fields?.siteLogo)"
        to="/"
        aria-label="navigate to home page"
      >
        <BasePicture
          role="presentation"
          aria-hidden="true"
          :src="data?.header?.fields?.siteLogo?.fields?.file?.url + `?w=176`"
          alt="WPN"
          :class="$style.picture"
          :width="
            data?.header?.fields?.siteLogo?.fields?.file?.details?.image?.width
          "
          :height="
            data?.header?.fields?.siteLogo?.fields?.file?.details?.image?.height
          "
          :img-class="$style.logo"
        />
      </NuxtLinkLocale>
      <nav v-if="data?.header?.fields?.navigation?.length" :class="$style.nav">
        <!-- desktop -->
        <div :class="$style.desktop">
          <ul :class="$style.navLvl1">
            <li v-for="n in data?.header?.fields?.navigation" :key="n?.sys?.id">
              <template
                v-if="
                  instanceOfNavigationGroup(n) &&
                  $visibility(n?.fields?.visibility)
                "
              >
                <div
                  :class="[
                    $style.navGroup,
                    activeGroupId === n?.sys?.id &&
                      isNavGroupOpen &&
                      $style.isActive,
                  ]"
                  @mouseenter="toggleNavGroup(n?.sys?.id)"
                  @mouseleave="resetState()"
                >
                  <button
                    type="button"
                    :class="[$style.navLink, $style.navButton]"
                  >
                    {{ n?.fields?.groupLabel }}
                    <IconChevronDown :class="$style.chevron" />
                  </button>
                  <ul :class="$style.navLvl2">
                    <li
                      v-for="l in n?.fields?.navigationLinks"
                      :key="l?.sys?.id"
                      @click="resetState()"
                    >
                      <div
                        v-if="
                          instanceOfNavigationGroup(l) &&
                          $visibility(l?.fields?.visibility)
                        "
                      >
                        <NuxtLinkLocale
                          :to="l?.fields?.groupLink"
                          :class="[
                            $style.navLink,
                            $style.groupTitle,
                            !l?.fields?.groupLink && $style.static,
                          ]"
                          @click="
                            sendEventToGa(
                              `${n?.fields?.groupLabel} > ${l?.fields?.groupLabel}`,
                              l?.fields?.groupLink,
                            )
                          "
                        >
                          {{ l?.fields?.groupLabel }}
                        </NuxtLinkLocale>
                        <ul :class="$style.navLvl3">
                          <li
                            v-for="ll in l?.fields?.navigationLinks"
                            :key="ll?.sys?.id"
                            @click="resetState()"
                          >
                            <NuxtLinkLocale
                              v-if="
                                instanceOfLinkable(ll) &&
                                $visibility(ll?.fields?.visibility)
                              "
                              :to="ll?.fields?.link"
                              :target="ll?.fields?.target"
                              :class="[
                                $style.navLink,
                                !ll?.fields?.link && $style.static,
                              ]"
                              @click="
                                sendEventToGa(
                                  `${n?.fields?.groupLabel} > ${l?.fields?.groupLabel} > ${ll?.fields?.label}`,
                                  ll?.fields?.link,
                                )
                              "
                            >
                              {{ ll?.fields?.label }}
                            </NuxtLinkLocale>
                          </li>
                        </ul>
                      </div>

                      <NuxtLinkLocale
                        v-else-if="
                          instanceOfLinkable(l) &&
                          $visibility(l?.fields?.visibility)
                        "
                        :to="l?.fields?.link"
                        :target="l?.fields?.target"
                        :class="[
                          $style.navLink,
                          !l?.fields?.link && $style.static,
                        ]"
                        @click="
                          sendEventToGa(
                            `${n?.fields?.groupLabel} > ${l?.fields?.label}`,
                            l?.fields?.link,
                          )
                        "
                      >
                        {{ l?.fields?.label }}
                      </NuxtLinkLocale>
                    </li>
                  </ul>
                </div>
              </template>
              <NuxtLinkLocale
                v-else-if="
                  instanceOfLinkable(n) && $visibility(n?.fields?.visibility)
                "
                no-prefetch
                :to="n?.fields?.link"
                :target="n?.fields?.target"
                :class="[$style.navLink, !n?.fields?.link && $style.static]"
                @click="sendEventToGa(n?.fields?.label, n?.fields?.link)"
              >
                {{ n?.fields?.label }}
              </NuxtLinkLocale>
            </li>
          </ul>
        </div>
      </nav>
      <div :class="$style.desktop" style="margin-left: auto">
        <ul
          v-if="!pageNotFound && computedUtilityLinks.length"
          :class="[$style.utils, $style.navLvl1]"
        >
          <li
            v-if="computedShowSearchInNavigation"
            :class="[$style.searchWrap, $style.utilsItems]"
          >
            <button
              :class="[$style.navLink, $style.utilButton]"
              @click="toggleShowDesktopSearch"
            >
              <i :class="$style.utilsIcon">
                <IconMagnifyingGlass :class="$style.searchIcon" />
              </i>
              {{ localizedStrings['search'] || 'Search' }}
            </button>
            <AppHeaderSearch v-if="showDesktopSearch" :class="$style.search" />
          </li>
          <li
            v-for="u in computedUtilityLinks"
            :key="u?.sys?.id"
            :class="$style.utilsItems"
          >
            <button
              v-if="u?.fields?.identifier === 'language-picker'"
              :class="[$style.navLink, $style.utilButton]"
              @click="onRequestLocaleSetter"
            >
              <i v-if="u?.fields?.media" :class="$style.utilsIcon">
                <img
                  :class="$style.utilsIcon"
                  :src="u?.fields?.media?.fields?.file?.url"
                  alt=""
                />
              </i>
              {{ u?.fields?.label }}
            </button>
            <NuxtLinkLocale
              v-else
              :to="u?.fields?.link"
              :class="[$style.navLink, $style.utilButton]"
            >
              <i v-if="u?.fields?.media" :class="$style.utilsIcon">
                <img
                  :class="$style.utilsIcon"
                  :src="u?.fields?.media?.fields?.file?.url"
                  alt=""
                />
              </i>
              {{ u?.fields?.label }}
            </NuxtLinkLocale>
          </li>
          <!-- Start Auth Buttons -->
          <ClientOnly>
            <template v-if="loggedIn">
              <li :class="$style.navLvl1">
                <div
                  :class="[
                    $style.navGroup,
                    activeGroupId === 'profile-nav' &&
                      isNavGroupOpen &&
                      $style.isActive,
                  ]"
                  @mouseenter="toggleNavGroup('profile-nav')"
                  @mouseleave="resetState()"
                >
                  <button :class="[$style.navLink, $style.utilButton]">
                    <i :class="[$style.profileIcon, $style.navButtonIcon]">
                      <IconProfile />
                    </i>
                    {{ displayName }}
                  </button>
                  <ul :class="[$style.navLvl2, $style.navLvl2Right]">
                    <li v-if="stores?.length">
                      <NuxtLinkLocale
                        to="/dashboard"
                        :class="[$style.navLink, $style.navButton]"
                      >
                        {{ localizedStrings['profile'] || 'Profile' }}
                      </NuxtLinkLocale>
                    </li>
                    <li>
                      <NuxtLinkLocale
                        to="https://myaccounts.wizards.com"
                        target="_blank"
                        :class="[$style.navLink, $style.navButton]"
                      >
                        {{
                          localizedStrings['wizards-account'] ||
                          'Wizards Account'
                        }}
                      </NuxtLinkLocale>
                    </li>
                    <li>
                      <button
                        :key="locale"
                        :class="[$style.navLink, $style.navButton]"
                        @click="handleLogout"
                      >
                        {{ localizedStrings['log-out'] || 'Log Out' }}
                      </button>
                    </li>
                  </ul>
                </div>
              </li>
            </template>
            <template v-else>
              <li :class="$style.navLvl1">
                <button
                  :key="locale"
                  :class="[
                    $style.navLink,
                    $style.utilButton,
                    $style.loginButton,
                  ]"
                  @click="handleLogin"
                >
                  <i :class="[$style.profileIcon, $style.navButtonIcon]">
                    <IconProfile />
                  </i>
                  {{ localizedStrings['log-in'] || 'Log In' }}
                </button>
              </li>
            </template>
            <!-- End Auth Buttons -->
          </ClientOnly>
        </ul>
      </div>

      <!-- mobile -->
      <div :class="$style.mobile" style="margin-left: auto">
        <div :class="$style.control">
          <button
            v-if="computedShowSearchInNavigation"
            :class="$style.menuButton"
            @click="toggleShowMobileSearch"
          >
            <IconMagnifyingGlass
              width="33"
              height="33"
              :class="$style.searchIcon"
            />

            <span :class="$style.menuButtonLabel">
              {{ localizedStrings['search'] || 'Search' }}
            </span>
          </button>
          <button
            aria-label="open mobile menu"
            :class="$style.menuButton"
            @click="isNavDrawerOpen = !isNavDrawerOpen"
          >
            <IconMenu v-if="!isNavDrawerOpen" width="40" height="40" />
            <IconClose v-if="isNavDrawerOpen" width="40" height="40" />
          </button>
          <div
            v-if="isNavDrawerOpen"
            :class="[
              $style.mobileNav,
              isNavDrawerOpen && $style.isNavDrawerOpen,
            ]"
          >
            <ul ontouchstart="" :class="$style.mobileLvl1">
              <li
                v-for="n in data?.header?.fields?.navigation"
                :key="n?.sys?.id"
              >
                <div
                  v-if="
                    instanceOfNavigationGroup(n) &&
                    $visibility(n?.fields?.visibility)
                  "
                  :class="$style.navGroup"
                >
                  <button
                    type="button"
                    :class="[
                      $style.navButton,
                      activeGroupId === n?.sys?.id && $style.isActive,
                    ]"
                    @click="toggleMobileNavGroup(n?.sys?.id)"
                  >
                    {{ n?.fields?.groupLabel }}
                    <i :class="$style.chevron">
                      <IconChevronUp
                        v-if="activeGroupId === n?.sys?.id && $style.isActive"
                      />
                      <IconChevronDown v-else />
                    </i>
                  </button>
                  <ul
                    v-if="activeGroupId === n?.sys?.id"
                    :class="$style.mobileLvl2"
                  >
                    <li
                      v-for="l in n?.fields?.navigationLinks"
                      :key="l?.sys?.id"
                      @click="resetState()"
                    >
                      <div
                        v-if="
                          instanceOfNavigationGroup(l) &&
                          $visibility(l?.fields?.visibility)
                        "
                      >
                        <NuxtLinkLocale
                          :to="l?.fields?.groupLink"
                          :class="[
                            $style.groupTitle,
                            !l?.fields?.groupLink && $style.static,
                          ]"
                          @click="
                            sendEventToGa(
                              `${n?.fields?.groupLabel} > ${l?.fields?.groupLabel}`,
                              l?.fields?.groupLink,
                            )
                          "
                        >
                          {{ l?.fields?.groupLabel }}
                        </NuxtLinkLocale>
                        <ul :class="$style.mobileLvl3">
                          <li
                            v-for="ll in l?.fields?.navigationLinks"
                            :key="ll?.sys?.id"
                            @click="resetState()"
                          >
                            <NuxtLinkLocale
                              v-if="
                                instanceOfLinkable(ll) &&
                                $visibility(ll?.fields?.visibility)
                              "
                              :to="ll?.fields?.link"
                              :target="ll?.fields?.target"
                              :class="[!ll?.fields?.link && $style.static]"
                              @click="
                                sendEventToGa(
                                  `${n?.fields?.groupLabel} > ${l?.fields?.groupLabel} > ${ll?.fields?.label}`,
                                  ll?.fields?.link,
                                )
                              "
                            >
                              {{ ll?.fields?.label }}
                            </NuxtLinkLocale>
                          </li>
                        </ul>
                      </div>

                      <NuxtLinkLocale
                        v-else-if="
                          instanceOfLinkable(l) &&
                          $visibility(l?.fields?.visibility)
                        "
                        :to="l?.fields?.link"
                        :target="l?.fields?.target"
                        :class="[!l?.fields?.link && $style.static]"
                        @click="
                          sendEventToGa(
                            `${n?.fields?.groupLabel} > ${l?.fields?.label}`,
                            l?.fields?.link,
                          )
                        "
                      >
                        {{ l?.fields?.label }}
                      </NuxtLinkLocale>
                    </li>
                  </ul>
                </div>
                <NuxtLinkLocale
                  v-else-if="
                    instanceOfLinkable(n) && $visibility(n?.fields?.visibility)
                  "
                  no-prefetch
                  :to="n?.fields?.link"
                  :target="n?.fields?.target"
                  :class="[!n?.fields?.link && $style.static]"
                  @click="
                    () => {
                      resetState()
                      sendEventToGa(n?.fields?.label, n?.fields?.link)
                    }
                  "
                >
                  {{ n?.fields?.label }}
                </NuxtLinkLocale>
              </li>
              <!-- Start Auth Buttons -->
              <template v-if="!pageNotFound">
                <ClientOnly>
                  <li v-if="loggedIn">
                    <div :class="$style.navGroup">
                      <span :class="$style.navButton">
                        <i :class="[$style.profileIcon, $style.navButtonIcon]">
                          <IconProfile />
                        </i>
                        {{ displayName }}
                      </span>
                      <ul :class="$style.mobileLvl2" style="display: block">
                        <li v-if="stores?.length">
                          <NuxtLinkLocale
                            to="/dashboard"
                            :class="$style.navButton"
                          >
                            {{ localizedStrings['profile'] || 'Profile' }}
                          </NuxtLinkLocale>
                        </li>
                        <li>
                          <NuxtLinkLocale
                            to="https://myaccounts.wizards.com"
                            target="_blank"
                            :class="$style.navButton"
                          >
                            {{
                              localizedStrings['wizards-account'] ||
                              'Wizards Account'
                            }}
                          </NuxtLinkLocale>
                        </li>
                        <li>
                          <button
                            :key="locale"
                            :class="$style.navButton"
                            @click="handleLogout"
                          >
                            {{ localizedStrings['log-out'] || 'Log Out' }}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </li>
                  <li v-if="!loggedIn">
                    <button
                      :key="locale"
                      :class="[$style.navButton, $style.loginButton]"
                      @click="handleLogin"
                    >
                      <i :class="[$style.profileIcon, $style.navButtonIcon]">
                        <IconProfile />
                      </i>
                      {{ localizedStrings['log-in'] || 'Log In' }}
                    </button>
                  </li>
                </ClientOnly>
              </template>
              <!-- End Auth Buttons -->
              <li v-if="!pageNotFound" :class="$style.mobileLanguage">
                <button
                  v-if="computedLanguageLink"
                  aria-label="change your language setting"
                  @click="onRequestLocaleSetter"
                >
                  <i
                    v-if="computedLanguageLink?.fields?.media"
                    :class="$style.utilsIcon"
                  >
                    <img
                      :class="$style.utilsIcon"
                      :src="
                        computedLanguageLink?.fields?.media?.fields?.file?.url
                      "
                      alt=""
                    />
                  </i>
                  {{ computedLanguageLink?.fields?.label }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <AppHeaderSearch v-if="showMobileSearch" :class="$style.mobileSearch" />
    </div>
    <!-- <JsonOverlay
      :data="localizedStrings"
      title="localizedStrings debug"
      open
      corner="br"
    /> -->
  </header>
</template>

<script setup lang="ts">
import type {
  TypeTheSiteHeaderSkeleton,
  TypeTheSiteHeaderWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const { handleLogin } = useAuthLogin()
const { locale } = useI18n()
const { $client } = useNuxtApp()
const { localizedStrings } = useLocalizedStrings()
const cookieStore = useCookieStore()

const { data } = await useAsyncData(
  `site-header-${cookieStore.countryCookie}-${cookieStore.languageCookie}`,
  async () => {
    // START CONTENTFUL DATA
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeTheSiteHeaderSkeleton>(
        {
          content_type: 'theSiteHeader',
          limit: 1,
          include: 10,
          locale: locale.value,
        },
      )
    const headerData = response?.items?.[0]
    // END CONTENTFUL DATA

    // START USER DATA
    const userStore = useUserStore()
    const { loggedIn, highestUserLevel } = storeToRefs(userStore)
    // END USER DATA

    // START NAVIGATION DATA
    let navigationToDisplay = headerData?.fields?.navigation
    const navigationLevels: (
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level1Navigation']
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level2Navigation']
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level3Navigation']
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level4Navigation']
    )[] = [
      headerData?.fields?.level1Navigation,
      headerData?.fields?.level2Navigation,
      headerData?.fields?.level3Navigation,
      headerData?.fields?.level4Navigation,
    ]

    // Check if there is level-gated content for the page
    const hasNavigationLevels = navigationLevels.some(
      (level) => level !== undefined,
    )
    // END NAVIGATION DATA

    if (hasNavigationLevels && loggedIn) {
      for (
        let currentLevel = highestUserLevel.value;
        currentLevel >= 1;
        currentLevel--
      ) {
        if (navigationLevels[currentLevel - 1]) {
          navigationToDisplay = navigationLevels[currentLevel - 1]
          break
        }
      }
    }

    // START UTILITY LINKS DATA
    let utilitylinksToDisplay = headerData?.fields?.utilityLinks
    const utilitylinksLevels: (
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level1UtilityLinks']
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level2UtilityLinks']
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level3UtilityLinks']
      | TypeTheSiteHeaderWithoutUnresolvableLinksResponse['fields']['level4UtilityLinks']
    )[] = [
      headerData?.fields?.level1UtilityLinks,
      headerData?.fields?.level2UtilityLinks,
      headerData?.fields?.level3UtilityLinks,
      headerData?.fields?.level4UtilityLinks,
    ]

    // Check if there is level-gated content for the page
    const hasUtilityLinksLevels = utilitylinksLevels.some(
      (level) => level !== undefined,
    )
    // END UTILITY LINKS DATA

    if (hasUtilityLinksLevels && loggedIn) {
      for (
        let currentLevel = highestUserLevel.value;
        currentLevel >= 1;
        currentLevel--
      ) {
        if (utilitylinksLevels[currentLevel - 1]) {
          utilitylinksToDisplay = utilitylinksLevels[currentLevel - 1]
          break
        }
      }
    }

    return {
      ...headerData,
      fields: {
        ...(headerData?.fields || {}),
        navigation: navigationToDisplay,
        utilityLinks: utilitylinksToDisplay,
      },
    }
  },
  {
    transform(input) {
      return {
        header: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)

defineProps<{
  pageNotFound?: boolean
}>()

const activeGroupId = ref<string | null>(null)
const isNavGroupOpen = ref(false)
const isNavDrawerOpen = ref(false)

// START User
const userStore = storeToRefs(useUserStore())
const { loggedIn, displayName, stores } = userStore
const { logout: handleLogout } = useWizardsLogout()
// END User

const showMobileSearch = ref(false)
const showDesktopSearch = ref(false)

const computedUtilityLinks = computed(() => {
  let utilityLinks = data.value?.header?.fields?.utilityLinks || []
  if (loggedIn?.value) {
    utilityLinks = utilityLinks.filter(
      (link) => link?.fields?.identifier !== 'log-in',
    )
  } else {
    utilityLinks = utilityLinks.filter(
      (link) =>
        link?.fields?.identifier !== 'log-out' &&
        link?.fields?.identifier !== 'my-account',
    )
  }
  return utilityLinks
})

const computedLanguageLink = computed(() => {
  return computedUtilityLinks.value?.find?.(
    (l) => l?.fields?.identifier === 'language-picker',
  )
})

const route = useRoute()
const computedShowSearchInNavigation = computed(
  () => loggedIn?.value && route.params.pathMatch !== 'search',
)

const toggleNavGroup = (id: string) => {
  activeGroupId.value = id
  isNavGroupOpen.value = !isNavGroupOpen.value
}

const toggleMobileNavGroup = (id: string) => {
  if (activeGroupId.value === id) {
    activeGroupId.value = null
  } else {
    activeGroupId.value = id
  }
}

const resetState = () => {
  activeGroupId.value = null
  isNavGroupOpen.value = false
  isNavDrawerOpen.value = false
}

const onRequestLocaleSetter = () => {
  const localeModalStore = useLocalModalStore()
  localeModalStore.showLocaleModal = true
}

const toggleShowMobileSearch = () =>
  (showMobileSearch.value = !showMobileSearch.value)
const toggleShowDesktopSearch = () =>
  (showDesktopSearch.value = !showDesktopSearch.value)

const sendEventToGa = (text?: string, link?: string) => {
  if (!text || !link) return

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'menu_navigation',
    eventParams: {
      menu_type: 'header',
      link_text: text,
      link_url: link,
    },
  })
}
</script>

<style lang="scss" module>
.wrap {
  background: var(--white);
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.content {
  display: flex;
  align-items: center;

  height: 60px;
  max-width: var(--site-header-content-max-width);
  padding: 0 var(--site-header-content-padding-x);
  margin: 0 auto;

  .desktop {
    display: var(--display-lg-and-up);
  }

  .mobile {
    display: var(--hidden-lg-and-up);
  }
}

.mobile {
  position: relative;
}

.control {
  display: flex;
}

.picture {
  display: flex !important;
}

.logo {
  @include fluid-type(78px, 84px);
  width: 1em;
  height: auto;
}

.nav {
  margin: 0 1em;

  .groupTitle {
    padding-bottom: 0.5em;
  }

  button {
    background: none;
    border: none;
  }
}

.navLink {
  // Reset default button
  appearance: none;
  background: none;
  border: none;

  color: var(--black);
  font-family: var(--base-font-family);
  font-size: to-rem(16px);
  font-weight: 600;

  display: inline-block;
  padding: 1em;
  height: 100%;

  &:hover {
    color: var(--purple);
  }

  &:active {
    color: var(--purple-dark);
  }
}

.static {
  &:hover {
    cursor: default;
    color: inherit;
  }
}

.chevron {
  display: inline-block;
  color: var(--purple-light);
  transform: translateY(-2px);
  width: 11px;
  height: 6px;
}

.navLvl1 {
  @include list-unstyled();
  display: flex;

  & > li {
    position: relative;
  }
}

.navLvl2 {
  @include list-unstyled();
  background: var(--white);
  border-bottom: 4px solid var(--purple-light);

  position: absolute;
  padding: 1em 2em;
  min-width: 200px;
  white-space: nowrap;

  display: none;

  .navGroup.isActive &,
  .navGroup.isActive:focus-within & {
    display: block;
  }

  a,
  button {
    padding: 0.5em 0;
  }
}

.navLvl2Right {
  right: 0;
  left: auto;
}

.navLvl3 {
  @include list-unstyled();
  padding: 0 1.25em;

  a,
  button {
    font-weight: normal;
    padding: 0.5em;
  }
}

.utils {
  height: 60px;

  .utilsIcon {
    width: 18px;
    height: 18px;

    margin-right: 10px;
  }
}

.utilsItems {
  border-right: 1px solid var(--gray-5);

  &:first-child {
    border-left: 1px solid var(--gray-5);
  }
}

.utilButton {
  background: none;
  border: none;

  color: var(--black);
  font-family: var(--base-font-family);
  font-size: 14px;
  text-decoration: none;

  display: inline-flex;
  align-items: center;

  height: 100%;
  padding: 1px 14px;

  &:hover {
    color: var(--purple);
  }

  &:active {
    color: var(--purple-dark);
  }
}

.profileIcon {
  display: flex;
  color: var(--purple);
  height: 18px;
  width: 18px;
}

.menuButton {
  background: none;
  border: none;
  color: var(--purple);
  display: flex;
  align-items: center;
  padding: 11px 9px 8px;

  .menuIcon {
    width: 40px;
    height: 40px;
  }
}

.menuButtonLabel {
  @include sr-only();
}

.mobileNav {
  background: var(--white);
  border-top: 1px solid var(--gray-5);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  position: fixed;
  top: 60px;
  bottom: 0;
  right: 0;
  width: 100vw;
  max-width: 400px;
  padding: 1.5em 1.25em 0;

  overflow: scroll;

  transform: translateX(120%);
  transition: transform cubic-bezier(0.16, 1, 0.29, 0.99) 0.667s;

  &.isNavDrawerOpen {
    transform: translateX(0%);
  }
}

.mobileLvl1 {
  font-size: 20px;
  padding: 0;

  li {
    display: flex;
  }

  li ~ li {
    border-top: 1px solid var(--black-alpha10);
  }

  .chevron {
    display: flex;
    transform: scale(1.5);
  }

  .navGroup {
    width: 100%;
  }

  button,
  a,
  span {
    background: none;
    border: none;

    color: var(--black);
    font-size: inherit;
    font-family: var(--base-font-family);
    font-weight: 600;
    display: flex;
    align-items: center;

    width: 100%;
    padding: 0.8em 0.5em;
    margin: 0;
  }

  button {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
}

.mobileLvl2 {
  display: none;
  font-size: 16px;
  padding-left: 1em;

  .navButton.isActive ~ &,
  .navButton:focus ~ & {
    display: block;
  }

  li ~ li {
    border-top: none;
  }

  button,
  a,
  span {
    padding: 0.65em 0.5em;
  }
}

.navButtonIcon {
  margin-right: 8px;
}

.mobileLvl3 {
  padding-left: 1em;

  a {
    padding: 0.65em 1.5em;
  }
}

.mobileLanguage {
  padding: 1em;
  margin: 0 auto;

  button {
    display: block;
    font-family: var(--base-font-family);
    font-size: 18px;

    img {
      transform: translateY(2px);
    }
  }
}

.mobileSearch {
  display: var(--hidden-lg-and-up);
  border-top: 1px solid var(--gray-2);
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
}

.searchWrap {
  position: relative;
}

.search {
  display: var(--display-lg-and-up);
  margin-top: 8px;
  position: absolute;
  top: 100%;
  right: 0;
  width: 375px;
}

.searchIcon {
  color: var(--purple);
}

button.loginButton {
  justify-content: center;
  background: var(--purple);
  color: var(--white);

  .profileIcon {
    color: var(--white);
  }

  &:hover,
  &:focus {
    background: var(--purple-light);
    color: var(--white);
  }

  &:active {
    background: var(--purple-dark);
    color: var(--white);
  }

  @include media('lg') {
    padding: 1px 30px;
    min-width: 200px;
    font-weight: bold;
    font-size: 21px;

    .profileIcon {
      height: 22px;
      width: 22px;
    }
  }
}
</style>
